import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Modal,
  Row,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { DEFAULT_PUBLIC_SURVEY_LOGO_URL } from "../../constants/brand";
import {
  ErrorAlert,
  formatDate,
  Loading,
  PageHeader,
  PlanLock,
  StatusBadge,
  UpgradeGrid,
  labelize,
} from "../../layout/secure/common";
import {
  createSurvey,
  currentUsage,
  getSurveyProperties,
  messageFromError,
  runSurveyAction,
  updateSurvey,
} from "../../utils/api";
import { featureIncluded, PREMIUM_FEATURES } from "../../utils/premium-features";
import PublicLinksCard from "./public-links-card";

const EMPTY_FORM = {
  name: "",
  description: "",
  authenticationMode: "anonymous",
  allowMultipleResponses: false,
  allowSubmittedResponseUpdates: true,
  publicLogoUrl: "",
  opensAt: "",
  closesAt: "",
};

const dateInputValue = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const toForm = item => ({
  name: item?.name || "",
  description: item?.description || "",
  publicLogoUrl: item?.publicLogoUrl || "",
  authenticationMode: item?.authenticationMode === "authenticated"
    ? "verified-email"
    : item?.authenticationMode || "anonymous",
  allowMultipleResponses: Boolean(item?.allowMultipleResponses),
  allowSubmittedResponseUpdates:
    item?.allowSubmittedResponseUpdates === undefined
      ? true
      : Boolean(item.allowSubmittedResponseUpdates),
  opensAt: dateInputValue(item?.opensAt),
  closesAt: dateInputValue(item?.closesAt),
});

const payloadFrom = (form, baseline = null) => {
  const payload = {
    ...form,
    opensAt: form.opensAt ? new Date(form.opensAt).toISOString() : null,
    closesAt: form.closesAt ? new Date(form.closesAt).toISOString() : null,
  };
  if (!baseline) return payload;
  return Object.fromEntries(
    Object.entries(payload).filter(([key]) => form[key] !== baseline[key]),
  );
};

function Placeholder({ mode, surveyId }) {
  const titles = {
    edit: "Survey editor",
    preview: "Survey preview",
    results: "Survey results",
    response: "Response report",
    report: "Aggregate report",
  };
  return (
    <>
      <PageHeader
        title={titles[mode] || "Survey"}
        description={`Work with survey ${surveyId || ""}.`}
        action={<Button as={Link} variant="outline-primary" to={`/dashboard/surveys/${surveyId}`}>Properties</Button>}
      />
      <section className="fera-panel">
        <p className="text-muted mb-0">This secure route is ready. Its specialized workflow is connected in the next delivery phase.</p>
      </section>
    </>
  );
}

function SurveyPage({ mode }) {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const creating = mode === "new";
  const [details, setDetails] = useState(null);
  const [usage, setUsage] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [baseline, setBaseline] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      if (creating) {
        setUsage(await currentUsage());
        return;
      }
      const [properties, usageData] = await Promise.all([
        getSurveyProperties(surveyId),
        currentUsage(),
      ]);
      const nextForm = toForm(properties.item);
      setDetails(properties);
      setForm(nextForm);
      setBaseline(nextForm);
      setUsage(usageData);
    } catch (requestError) {
      setError(messageFromError(requestError, "Survey properties could not be loaded."));
    }
  }, [creating, surveyId]);

  useEffect(() => { load(); }, [load]);

  const changed = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [baseline, form],
  );

  const change = event => {
    const { name, type, checked, value } = event.target;
    setForm(current => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const save = async event => {
    event.preventDefault();
    if (!changed && !creating) return;
    setSaving(true);
    setError("");
    try {
      const result = creating
        ? await createSurvey(payloadFrom(form))
        : await updateSurvey(surveyId, payloadFrom(form, baseline));
      toast.success(creating ? "Draft survey created." : "Survey properties saved.");
      if (creating) {
        navigate(`/dashboard/surveys/${result.item.uuid}`, { replace: true });
      } else {
        const nextForm = toForm(result.item);
        setForm(nextForm);
        setBaseline(nextForm);
        setDetails(current => ({ ...current, item: result.item }));
      }
    } catch (requestError) {
      setError(messageFromError(requestError, "Survey properties could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const perform = async action => {
    if (["archive", "delete", "restore"].includes(action)) {
      setConfirmation(action);
      return;
    }
    setActionBusy(true);
    setError("");
    try {
      const result = await runSurveyAction(surveyId, action);
      toast.success(action === "duplicate"
        ? "Draft copy created."
        : action === "publish"
          ? (details.item.status === "closed" ? "Survey reopened." : "Survey published.")
          : `Survey ${action}d.`);
      if (action === "duplicate") {
        navigate(`/dashboard/surveys/${result.item.uuid}`);
      } else {
        await load();
      }
    } catch (requestError) {
      setError(messageFromError(requestError, `The survey could not be ${action}d.`));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmAction = async () => {
    const action = confirmation;
    setConfirmation(null);
    setActionBusy(true);
    setError("");
    try {
      await runSurveyAction(surveyId, action);
      toast.success(action === "delete" ? "Survey moved to deleted." : `Survey ${action}d.`);
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, `The survey could not be ${action}d.`));
    } finally {
      setActionBusy(false);
    }
  };

  if (!["new", "detail"].includes(mode)) {
    return <Placeholder mode={mode} surveyId={surveyId} />;
  }
  if (!creating && !details && !error) return <Loading label="Loading survey properties" />;

  const item = details?.item;
  const activeFull = usage?.surveys?.remaining === 0;
  const customBranding = featureIncluded(usage, "customBranding");
  const canPublish = ["draft", "closed"].includes(item?.status);

  return (
    <>
      <PageHeader
        title={creating ? "Create survey" : item?.name || "Survey properties"}
        description={creating
          ? "Create a draft now. Publishing is a separate, capacity-checked action."
          : "Manage lifecycle, response identity, schedule, and content access."}
        action={(
          <div className="fera-page-buttons">
            <Button as={Link} variant="outline-secondary" to="/dashboard/surveys">Back</Button>
            <Button type="submit" form="survey-properties-form" disabled={saving || (!creating && !changed)}>
              {saving ? "Saving\u2026" : creating ? "Create draft" : "Save changes"}
            </Button>
          </div>
        )}
      />
      <ErrorAlert error={error} onRetry={load} />
      {!creating && activeFull && canPublish ? (
        <Alert variant="warning">
          Your active-survey allowance is full. Save this draft, then close another active survey or upgrade before publishing.
        </Alert>
      ) : null}
      {!creating ? (
        <section className="fera-panel fera-lifecycle-bar">
          <div>
            <span>Status</span>
            <StatusBadge status={item.status} />
            <small>Updated {formatDate(item.updatedAt)}</small>
          </div>
          <div className="fera-row-actions">
            {canPublish ? (
              <Button disabled={actionBusy} onClick={() => perform("publish")}>
                {item.status === "closed" ? "Reopen" : "Publish"}
              </Button>
            ) : null}
            {item.status === "published" ? (
              <Button variant="outline-secondary" disabled={actionBusy} onClick={() => perform("close")}>Close</Button>
            ) : null}
            {!["deleted", "archived"].includes(item.status) ? (
              <Button variant="outline-secondary" disabled={actionBusy} onClick={() => perform("archive")}>Archive</Button>
            ) : null}
            {item.status === "deleted" ? (
              <Button variant="outline-primary" disabled={actionBusy} onClick={() => perform("restore")}>Restore as closed</Button>
            ) : (
              <Button variant="outline-danger" disabled={actionBusy} onClick={() => perform("delete")}>Delete</Button>
            )}
            {item.status !== "deleted" ? (
              <Button variant="outline-primary" disabled={actionBusy} onClick={() => perform("duplicate")}>Duplicate</Button>
            ) : null}
            {["draft", "closed"].includes(item.status) ? (
              <Button as={Link} variant="outline-primary" to={`/dashboard/surveys/${surveyId}/edit`}>Open editor</Button>
            ) : null}
            <Button as={Link} variant="outline-primary" to={`/dashboard/surveys/${surveyId}/preview`}>Preview</Button>
            <Button as={Link} variant="outline-primary" to={`/dashboard/surveys/${surveyId}/results`}>View results</Button>
          </div>
        </section>
      ) : null}
      <Form id="survey-properties-form" onSubmit={save}>
        <Row className="g-4">
          <Col xl="8">
            <section className="fera-panel">
              <div className="fera-panel-heading">
                <div><p className="fera-eyebrow">Properties</p><h2>Survey details</h2></div>
              </div>
              <Row className="g-3">
                <Col xs="12">
                  <Form.Group controlId="survey-name">
                    <Form.Label>Survey name</Form.Label>
                    <Form.Control
                      name="name"
                      value={form.name}
                      onChange={change}
                      maxLength={150}
                      required
                      autoFocus={creating}
                    />
                  </Form.Group>
                </Col>
                <Col xs="12">
                  <Form.Group controlId="survey-description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={form.description}
                      onChange={change}
                      maxLength={20000}
                    />
                    <Form.Text>Shown to administrators; responder-facing introduction is managed in the editor.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </section>
            <section className="fera-panel mt-4">
              <div className="fera-panel-heading">
                <div><p className="fera-eyebrow">Availability</p><h2>Schedule</h2></div>
              </div>
              <Row className="g-3">
                <Col md="6">
                  <Form.Group controlId="survey-opens-at">
                    <Form.Label>Opens at</Form.Label>
                    <Form.Control type="datetime-local" name="opensAt" value={form.opensAt} onChange={change} />
                  </Form.Group>
                </Col>
                <Col md="6">
                  <Form.Group controlId="survey-closes-at">
                    <Form.Label>Closes at</Form.Label>
                    <Form.Control type="datetime-local" name="closesAt" value={form.closesAt} onChange={change} />
                  </Form.Group>
                </Col>
              </Row>
            </section>
          </Col>
          <Col xl="4">
            <section className="fera-panel">
              <div className="fera-panel-heading">
                <div><p className="fera-eyebrow">Identity</p><h2>Response policy</h2></div>
              </div>
              <Form.Group controlId="survey-authentication-mode">
                <Form.Label>Authentication mode</Form.Label>
                <Form.Select name="authenticationMode" value={form.authenticationMode} onChange={change}>
                   <option value="anonymous">Anonymous</option>
                   <option value="verified-email">Verified email</option>
                   <option value="invitation">Invitation only</option>
                </Form.Select>
              </Form.Group>
              <Form.Check
                className="mt-4"
                type="switch"
                id="survey-multiple"
                name="allowMultipleResponses"
                checked={form.allowMultipleResponses}
                onChange={change}
                label="Allow multiple responses per person"
              />
              <Form.Check
                className="mt-3"
                type="switch"
                id="survey-updates"
                name="allowSubmittedResponseUpdates"
                checked={form.allowSubmittedResponseUpdates}
                onChange={change}
                label="Allow people to change their submitted responses"
              />
            </section>
            <section className="fera-panel mt-4">
              <div className="fera-panel-heading">
                <div><p className="fera-eyebrow">Appearance</p><h2>Public survey logo</h2></div>
                {customBranding
                  ? <span className="text-muted small">Complete and Beyond feature</span>
                  : (
                    <PlanLock title={PREMIUM_FEATURES.customBranding.description}>
                      {PREMIUM_FEATURES.customBranding.label}
                    </PlanLock>
                  )}
              </div>
              <img
                className="fera-survey-logo-preview"
                src={(customBranding && form.publicLogoUrl) || DEFAULT_PUBLIC_SURVEY_LOGO_URL}
                alt="Current public survey logo"
              />
              <Form.Group controlId="survey-public-logo-url" className="mt-3">
                <Form.Label>Logo URL</Form.Label>
                <Form.Control
                  type="url"
                  inputMode="url"
                  name="publicLogoUrl"
                  value={form.publicLogoUrl}
                  placeholder={DEFAULT_PUBLIC_SURVEY_LOGO_URL}
                  onChange={change}
                  disabled={!customBranding}
                />
                <Form.Text>
                  {customBranding
                    ? "Use an HTTPS image URL. Leave blank to use the dark Feradel logo."
                    : "Custom survey branding is available with Complete and Beyond. The dark Feradel logo is used on this plan."}
                </Form.Text>
              </Form.Group>
            </section>
            {!creating ? (
              <section className="fera-panel mt-4">
                <div className="fera-panel-heading">
                  <div><p className="fera-eyebrow">Content</p><h2>Survey structure</h2></div>
                </div>
                <dl className="fera-properties">
                  <div><dt>Sections</dt><dd>{details?.content?.sections || 0}</dd></div>
                  <div><dt>Questions</dt><dd>{details?.content?.questions || 0}</dd></div>
                  <div><dt>Responses</dt><dd>{details?.content?.responses || 0}</dd></div>
                  <div><dt>Editor version</dt><dd>{item.editorVersion}</dd></div>
                </dl>
              </section>
            ) : null}
          </Col>
        </Row>
      </Form>
      {!creating && item ? <PublicLinksCard survey={item} /> : null}
      {!creating && details?.audit?.length ? (
        <section className="fera-panel mt-4">
          <div className="fera-panel-heading">
            <div><p className="fera-eyebrow">Audit</p><h2>Lifecycle events</h2></div>
          </div>
          <div className="fera-audit-list">
            {details.audit.map(event => (
              <div key={event.id}>
                <span>{event.reason ? labelize(event.reason) : labelize(event.type.split(".").pop())}</span>
                <small>{formatDate(event.occurredAt)} &middot; Delivery {labelize(event.status)}</small>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {!creating && activeFull && canPublish ? <UpgradeGrid access={usage?.access} /> : null}
      <Modal show={Boolean(confirmation)} onHide={() => setConfirmation(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmation === "delete" ? "Delete survey?" : `${labelize(confirmation)} survey?`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmation === "delete"
            ? "The public link and future invitations will be disabled. Historical responses and reports remain available according to retention policy."
            : confirmation === "restore"
              ? "The survey will return in the closed state. Restoring never publishes it automatically."
              : "The survey will stop accepting responses and remain available in Archived."}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmation(null)}>Cancel</Button>
          <Button variant={confirmation === "delete" ? "danger" : "primary"} onClick={confirmAction}>Confirm</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SurveyPage;
